/**
 * SAFER-i™ Authentication Utilities
 * Unified auth handling for JWT tokens and OAuth sessions
 *
 * Token Storage Strategy (security tightening — Feb 2026):
 *   * JWT and OAuth tokens are stored in sessionStorage ONLY.
 *   * Closing the browser/tab clears the session and forces a fresh login.
 *   * Backend issues 60-min JWTs; frontend silently refreshes via
 *     POST /api/auth/refresh while the user is active.
 *   * 10-min idle timer (see <IdleLogoutManager/>) hard-logs-out anyone away.
 *   * localStorage is kept clear of auth keys; we still call removeItem there
 *     defensively so any stale token from before this change is wiped on next
 *     touch.
 */

const TOKEN_KEY = 'safer_token';
const USER_KEY = 'safer_user';
const SESSION_KEY = 'safer_session_token'; // legacy, retained for cleanup

/**
 * Get the current access token. sessionStorage is now the only source of truth;
 * any stray localStorage token is wiped to enforce the session-only policy.
 */
export const getToken = () => {
  // Defensive: clear any legacy localStorage token from prior versions
  if (localStorage.getItem(TOKEN_KEY)) {
    localStorage.removeItem(TOKEN_KEY);
  }
  return sessionStorage.getItem(TOKEN_KEY);
};

/**
 * Get the current user data
 */
export const getUser = () => {
  // Read from sessionStorage first; fall back to localStorage for one-shot
  // migration if a user is mid-session when this change ships.
  const userStr = sessionStorage.getItem(USER_KEY) || localStorage.getItem(USER_KEY);
  if (userStr) {
    try {
      return JSON.parse(userStr);
    } catch (e) {
      console.error('Failed to parse user data:', e);
      return null;
    }
  }
  return null;
};

/**
 * Store authentication data (token + user)
 * Used after successful login (both JWT and OAuth).
 *
 * sessionStorage-only: closing the browser/tab clears these and forces
 * a fresh login on next visit (matches enterprise security policy).
 */
export const setAuth = (token, userData) => {
  if (token) {
    sessionStorage.setItem(TOKEN_KEY, token);
    // Defensive cleanup of any legacy localStorage token
    localStorage.removeItem(TOKEN_KEY);
  }

  if (userData) {
    const userToStore = {
      user_id: userData.user_id,
      email: userData.email,
      full_name: userData.full_name,
      display_name: userData.display_name,
      role: userData.role,
      org_id: userData.org_id,
      phone: userData.phone,
      avatar_url: userData.avatar_url,
      email_verified: userData.email_verified || false,
      account_type: userData.account_type,
      freelancer_id: userData.freelancer_id,
    };
    sessionStorage.setItem(USER_KEY, JSON.stringify(userToStore));
    localStorage.removeItem(USER_KEY);
  }
};

/**
 * Update user data without changing token
 */
export const updateUser = (userData) => {
  const existing = getUser();
  const updated = { ...existing, ...userData };
  sessionStorage.setItem(USER_KEY, JSON.stringify(updated));
  return updated;
};

/**
 * Clear all authentication data
 */
export const clearAuth = () => {
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(USER_KEY);
  sessionStorage.removeItem(SESSION_KEY);
  // Defensive cleanup of any legacy localStorage tokens
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(SESSION_KEY);
  // Drop trusted-device tokens too — explicit logout always revokes trust.
  localStorage.removeItem('safer_trusted_token');
  localStorage.removeItem('safer_trusted_until');

  // Also try to clear any session cookies (won't work for httpOnly, but try)
  document.cookie = 'session_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = () => {
  const token = getToken();
  const user = getUser();
  return !!(token && user);
};

/**
 * Check if token might be expired (basic check)
 * For JWT tokens, we can decode and check exp claim
 */
export const isTokenExpired = () => {
  const token = getToken();
  if (!token) return true;
  
  try {
    // JWT tokens have 3 parts separated by dots
    const parts = token.split('.');
    if (parts.length !== 3) return false; // Not a JWT, assume valid
    
    // Decode the payload (middle part)
    const payload = JSON.parse(atob(parts[1]));
    
    // Check expiration
    if (payload.exp) {
      const expiryDate = new Date(payload.exp * 1000);
      const now = new Date();
      
      // Consider expired if less than 5 minutes remaining
      const bufferMs = 5 * 60 * 1000;
      return expiryDate.getTime() - bufferMs < now.getTime();
    }
    
    return false;
  } catch (e) {
    console.error('Error checking token expiry:', e);
    return false;
  }
};

/**
 * Role checking utilities
 */
export const hasRole = (allowedRoles) => {
  const user = getUser();
  if (!user || !user.role) return false;
  return allowedRoles.includes(user.role);
};

export const canManageUsers = () => hasRole(['HQ', 'ADMIN']);
export const canManageForms = () => hasRole(['HQ', 'ADMIN', 'SUPERVISOR']);
export const canManageProjects = () => hasRole(['HQ', 'DIRECTOR', 'EXECUTIVE', 'MANAGER', 'ADMIN']);
export const canViewBilling = () => hasRole(['HQ']);
export const canViewReports = () => hasRole(['HQ', 'ADMIN', 'EXECUTIVE', 'MANAGER']);
export const isWorker = () => hasRole(['WORKER']);
export const isSupervisor = () => hasRole(['SUPERVISOR', 'ADMIN', 'HQ']);

/**
 * Role groups (aligned with backend require_role gates)
 *  - Oversight: operational leadership (Supervisor → HQ) — team/project/compliance ops
 *  - Strategic: senior leadership (Manager/Director/HQ + platform Admin) — reports, exec intel
 *  - Platform admin: HQ + Admin — org configuration only
 */
export const canViewOversight = () => hasRole(['HQ', 'ADMIN', 'EXECUTIVE', 'MANAGER', 'DIRECTOR', 'SUPERVISOR']);
export const canViewStrategic = () => hasRole(['HQ', 'ADMIN', 'EXECUTIVE', 'MANAGER', 'DIRECTOR']);
export const isPlatformAdmin = () => hasRole(['HQ', 'ADMIN']);
// "Manager and above" — controls the dashboard time-range pivot
export const canControlDashboardPeriod = () => hasRole(['HQ', 'ADMIN', 'EXECUTIVE', 'MANAGER', 'DIRECTOR']);

/**
 * Get user display name
 */
export const getUserDisplayName = () => {
  const user = getUser();
  if (!user) return 'User';
  return user.display_name || user.full_name || user.email?.split('@')[0] || 'User';
};

/**
 * Get user initials for avatar
 */
export const getUserInitials = () => {
  const user = getUser();
  if (!user) return 'U';
  
  const name = user.display_name || user.full_name || user.email || 'U';
  const parts = name.split(' ');
  
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

export default {
  getToken,
  getUser,
  setAuth,
  updateUser,
  clearAuth,
  isAuthenticated,
  isTokenExpired,
  hasRole,
  canManageUsers,
  canManageForms,
  canManageProjects,
  canViewBilling,
  canViewReports,
  canViewOversight,
  canViewStrategic,
  isPlatformAdmin,
  canControlDashboardPeriod,
  isWorker,
  isSupervisor,
  getUserDisplayName,
  getUserInitials
};
